import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, ChevronUp, ChevronDown, CornerDownRight } from "lucide-react";
import { toast } from "sonner";

const STATIC_LINKS = [
  { label: "Home", url: "/" },
  { label: "Properties (home anchor)", url: "/#properties" },
  { label: "Contact (home anchor)", url: "/#contact" },
  { label: "Blog & Stories", url: "/blog" },
  { label: "Partner with us", url: "/partner-with-us" },
];

function LinkPicker({ options, onPick }) {
  return (
    <Select value="" onValueChange={(v) => { const o = options.flatMap((g) => g.items).find((x) => x.url === v); if (o) onPick(o); }}>
      <SelectTrigger className="w-[130px] shrink-0 text-xs" data-testid="menu-link-picker">
        <SelectValue placeholder="Pick a page" />
      </SelectTrigger>
      <SelectContent>
        {options.map((g) => g.items.length > 0 && (
          <SelectGroup key={g.group}>
            <SelectLabel>{g.group}</SelectLabel>
            {g.items.map((o) => <SelectItem key={o.url} value={o.url}>{o.label}</SelectItem>)}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

function ItemRow({ item, onChange, onDelete, onMove, canUp, canDown, options, isChild, testId }) {
  return (
    <div className={`flex items-center gap-2 ${isChild ? "pl-8" : ""}`} data-testid={testId}>
      {isChild && <CornerDownRight className="w-4 h-4 text-zinc-400 shrink-0" />}
      <Switch checked={item.enabled !== false} onCheckedChange={(v) => onChange({ ...item, enabled: v })} data-testid={`${testId}-toggle`} />
      <Input value={item.label || ""} onChange={(e) => onChange({ ...item, label: e.target.value })} placeholder="Label" className={item.enabled === false ? "opacity-50" : ""} data-testid={`${testId}-label`} />
      <Input value={item.url || ""} onChange={(e) => onChange({ ...item, url: e.target.value })} placeholder={isChild ? "URL" : "URL (blank = label only)"} className={item.enabled === false ? "opacity-50" : ""} data-testid={`${testId}-url`} />
      <LinkPicker options={options} onPick={(o) => onChange({ ...item, url: o.url, label: item.label?.trim() ? item.label : o.label })} />
      <div className="flex flex-col shrink-0">
        <button onClick={() => onMove(-1)} disabled={!canUp} className="text-zinc-500 disabled:opacity-20 hover:text-zinc-900" data-testid={`${testId}-up`}><ChevronUp className="w-4 h-4" /></button>
        <button onClick={() => onMove(1)} disabled={!canDown} className="text-zinc-500 disabled:opacity-20 hover:text-zinc-900" data-testid={`${testId}-down`}><ChevronDown className="w-4 h-4" /></button>
      </div>
      <button onClick={onDelete} className="px-2 text-red-600 shrink-0" data-testid={`${testId}-delete`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

export default function AdminMenu() {
  const [menu, setMenu] = useState(null);
  const [options, setOptions] = useState([{ group: "Site", items: STATIC_LINKS }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setMenu((r.data?.header_menu || []).map((m) => ({ enabled: true, children: [], ...m }))));
    Promise.all([
      api.get("/admin/properties").catch(() => ({ data: [] })),
      api.get("/admin/seo-pages").catch(() => ({ data: [] })),
      api.get("/admin/pages").catch(() => ({ data: [] })),
    ]).then(([props, seo, pages]) => {
      setOptions([
        { group: "Site", items: STATIC_LINKS },
        { group: "Properties", items: (props.data || []).map((p) => ({ label: p.name || p.slug, url: `/${p.slug}` })) },
        { group: "SEO Pages", items: (seo.data || []).map((p) => ({ label: p.h1 || p.slug, url: `/${p.slug}` })) },
        { group: "Custom Pages", items: (pages.data || []).map((p) => ({ label: p.title || p.slug, url: `/${p.slug}` })) },
      ]);
    });
  }, []);

  if (!menu) return <div>Loading…</div>;

  const move = (arr, i, dir) => {
    const n = [...arr];
    const j = i + dir;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  };
  const setItem = (i, v) => setMenu((m) => m.map((x, idx) => (idx === i ? v : x)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { header_menu: menu });
      toast.success("Menu saved — changes are live on the site");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Navigation</div>
          <h1 className="font-display text-4xl mt-3">Header Menu</h1>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm rounded-md" data-testid="menu-save">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <p className="text-sm text-zinc-500 mt-4 max-w-2xl">
        Toggle items on or off without deleting them, reorder with the arrows, and add sub-pages under any item —
        they appear as a dropdown on desktop and an expandable list in the mobile menu. Leave a parent URL blank to make it a label-only dropdown.
      </p>

      <div className="mt-8 max-w-3xl space-y-5">
        {menu.map((m, i) => (
          <div key={i} className="border border-zinc-200 rounded-lg p-4 space-y-2.5 bg-white" data-testid={`menu-item-${i}`}>
            <ItemRow
              item={m}
              onChange={(v) => setItem(i, v)}
              onDelete={() => setMenu(menu.filter((_, idx) => idx !== i))}
              onMove={(dir) => setMenu(move(menu, i, dir))}
              canUp={i > 0}
              canDown={i < menu.length - 1}
              options={options}
              testId={`menu-row-${i}`}
            />
            {(m.children || []).map((c, j) => (
              <ItemRow
                key={j}
                item={c}
                isChild
                onChange={(v) => setItem(i, { ...m, children: m.children.map((x, idx) => (idx === j ? v : x)) })}
                onDelete={() => setItem(i, { ...m, children: m.children.filter((_, idx) => idx !== j) })}
                onMove={(dir) => setItem(i, { ...m, children: move(m.children, j, dir) })}
                canUp={j > 0}
                canDown={j < m.children.length - 1}
                options={options}
                testId={`menu-row-${i}-child-${j}`}
              />
            ))}
            <button
              onClick={() => setItem(i, { ...m, children: [...(m.children || []), { label: "", url: "", enabled: true }] })}
              className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border rounded-md ml-8 text-zinc-600 hover:text-zinc-900"
              data-testid={`menu-add-child-${i}`}
            >
              <Plus className="w-3 h-3" /> Add sub-page
            </button>
          </div>
        ))}
        <button
          onClick={() => setMenu([...menu, { label: "", url: "", enabled: true, children: [] }])}
          className="text-sm inline-flex items-center gap-1.5 px-4 py-2 border rounded-md"
          data-testid="menu-add-item"
        >
          <Plus className="w-4 h-4" /> Add menu item
        </button>
      </div>
    </div>
  );
}
